import { useEffect, useState } from 'react';
import type {
  IncidenciaPortal,
  NivelIncidencia,
  PortalPropiedad,
} from '@matias-asset-os/contracts';
import { formatearDia, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { fechaLarga, FotoVilla, semillaDe } from '../componentes.js';
import { Modal } from '../componentes/Modal.js';
import { Icono } from '../componentes/Iconos.js';

/** El color de cada estado del semaforo, con las clases que existen. */
const CLASE_SEMAFORO: Record<string, string> = {
  VERDE: 'verde',
  AMARILLO: 'ambar',
  ROJO: 'rojo',
};

/**
 * PORTAL DEL PROPIETARIO
 *
 * Lo que ve el dueño de la casa, y nada más. No hay coste interno, ni margen,
 * ni quién de nuestro equipo hizo qué: eso ni siquiera llega al navegador,
 * porque el portal se sirve por sus propias rutas.
 *
 * El orden de la página es el de sus preguntas, en ese orden:
 *   1. ¿está mi casa bien?      → semáforo y tres cifras
 *   2. ¿qué está pasando?       → sus incidencias, con fotos y conversación
 *   3. voy a ir                 → avisa su estancia
 *   4. necesito algo            → reporta una incidencia o escribe al equipo
 */

/*
  UN NUMERO CORTO Y UNA FECHA NO PIDEN EL MISMO TAMANO.

  El valor se escribe grande porque casi siempre son tres caracteres —«9», «Si»,
  «0,00 €»—. Una fecha entera con ese cuerpo no cabe en la tarjeta y se parte en
  dos lineas, y «23-09-» encima de «2026» se lee peor que la fecha pequena.
*/
function Kpi({ etiqueta, valor, pie }: { etiqueta: string; valor: string; pie?: string }) {
  return (
    <div className="kpi">
      <span className="kpi-etiqueta">{etiqueta}</span>
      <span className={`kpi-valor${valor.length > 6 ? ' kpi-valor-largo' : ''}`}>{valor}</span>
      {pie && <span className="kpi-pie">{pie}</span>}
    </div>
  );
}

/**
 * LAS SUYAS, ABIERTAS O RESUELTAS.
 *
 * Una casa con dos años de historia acumula cuarenta incidencias, y las quince
 * de arriba son casi todas cosas ya terminadas. Quien entra a mirar quiere una
 * de dos cosas: qué sigue pendiente, o buscar algo que ya se arregló. Mezcladas
 * no sirven para ninguna de las dos.
 *
 * Empieza en «abiertas», que es lo que se viene a ver. El contador de cada
 * botón dice cuántas hay sin tener que pulsarlo.
 */
function IncidenciasDelDueno({
  idioma,
  incidencias,
  soloMirar,
  onCambio,
}: {
  idioma: Idioma;
  incidencias: PortalPropiedad['incidencias'];
  soloMirar: boolean;
  onCambio: () => void;
}) {
  type Filtro = 'ABIERTAS' | 'RESUELTAS' | 'TODAS';
  const abiertas = incidencias.filter((i) => i.abierta).length;
  /*
    Si no queda ninguna abierta, empezar en «abiertas» sería enseñar una lista
    vacía sobre una casa que tiene historia. En ese caso se abre en «todas».
  */
  const [filtro, setFiltro] = useState<Filtro>(abiertas > 0 ? 'ABIERTAS' : 'TODAS');

  const BOTONES: Array<[Filtro, number]> = [
    ['ABIERTAS', abiertas],
    ['RESUELTAS', incidencias.length - abiertas],
    ['TODAS', incidencias.length],
  ];
  const visibles = incidencias.filter((i) =>
    filtro === 'TODAS' ? true : filtro === 'ABIERTAS' ? i.abierta : !i.abierta,
  );

  return (
    <>
      <div className="conmutador-vista portal-filtro" role="group" aria-label={t(idioma, 'portal.misIncidencias')}>
        {BOTONES.map(([clave, cuantas]) => (
          <button
            key={clave}
            type="button"
            aria-pressed={filtro === clave}
            onClick={() => setFiltro(clave)}
          >
            {t(idioma, `portal.filtro${clave}`)} <span className="pastilla neutra">{cuantas}</span>
          </button>
        ))}
      </div>
      {visibles.length === 0 ? (
        <p className="suave">{t(idioma, 'portal.sinEnEsteFiltro')}</p>
      ) : (
        <div className="portal-incidencias">
          {visibles.map((i) => (
            <FilaIncidencia
              key={i.id}
              idioma={idioma}
              incidencia={i}
              soloMirar={soloMirar}
              onCambio={onCambio}
            />
          ))}
        </div>
      )}
    </>
  );
}

/** Una incidencia del portal, plegada; se abre para ver fotos y conversación. */
function FilaIncidencia({
  idioma,
  incidencia,
  soloMirar,
  onCambio,
}: {
  idioma: Idioma;
  incidencia: IncidenciaPortal;
  /** Un promotor abre la ficha, la lee y no deja rastro en ella. */
  soloMirar: boolean;
  onCambio: () => void;
}) {
  const [abierta, setAbierta] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [titulo, setTitulo] = useState(incidencia.titulo);
  const [descripcion, setDescripcion] = useState(incidencia.descripcion ?? '');
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enviar = () => {
    const cuerpo = texto.trim();
    if (!cuerpo) return;
    setEnviando(true);
    setError(null);
    api
      .portalMensaje(incidencia.id, cuerpo)
      .then(() => {
        setTexto('');
        onCambio();
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setEnviando(false));
  };

  /** Corrige lo que escribio. Solo mientras la incidencia siga abierta. */
  const guardarTexto = () => {
    setError(null);
    api
      .portalEditarIncidencia(incidencia.id, {
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || null,
      })
      .then(() => {
        setEditando(false);
        onCambio();
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'portal.noSeGuardo')),
      );
  };

  /*
   * Las fotos se suben una a una y con la incidencia ya creada. Si una falla,
   * las anteriores estan guardadas: se pierde esa foto, no el parte.
   */
  const subirFotos = (ficheros: File[]) => {
    if (ficheros.length === 0) return;
    setSubiendo(true);
    setError(null);
    void (async () => {
      try {
        for (const f of ficheros) await api.subirAdjunto('INCIDENCIA', incidencia.id, f);
        onCambio();
      } catch (f: unknown) {
        setError(
          f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'portal.noSeSubio'),
        );
      } finally {
        setSubiendo(false);
      }
    })();
  };

  return (
    <div className={`portal-inc ${incidencia.abierta ? 'viva' : 'cerrada'}`}>
      <button
        type="button"
        className="portal-inc-cabeza"
        aria-expanded={abierta}
        onClick={() => setAbierta((v) => !v)}
      >
        {incidencia.fotos[0] && (
          <img src={api.urlAdjunto(incidencia.fotos[0])} alt="" className="portal-inc-mini" />
        )}
        <span className="crecer">
          <b>{incidencia.titulo}</b>
          {/*
            EL NIVEL, COMO LO VE EL EQUIPO. N1 es urgencia y N4 una mejora
            estetica: al dueño le dice cuanto corre cada cosa sin tener que
            preguntarlo. Va en la linea de la fecha para no quitarle ancho al
            titulo en el movil.
          */}
          <small className="portal-inc-meta">
            <span
              className={`portal-nivel n${incidencia.nivel}`}
              title={t(idioma, `incidencia.nivel.${incidencia.nivel}`)}
            >
              N{incidencia.nivel}
            </span>
            {fechaLarga(incidencia.fecha, idioma)}
          </small>
        </span>
        <span className={`pastilla ${incidencia.abierta ? 'ambar' : 'verde'}`}>
          {t(idioma, incidencia.abierta ? 'portal.abierta' : 'portal.resuelta')}
        </span>
        <span aria-hidden="true">{abierta ? '⌃' : '⌄'}</span>
      </button>

      {abierta && (
        <div className="portal-inc-cuerpo">
          <p className="codigo-inc">{incidencia.codigo}</p>

          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}

          {/* Corregir lo que uno mismo escribio, mientras siga abierta. */}
          {editando ? (
            <div className="portal-editar">
              <div className="campo">
                <label htmlFor={`ed-tit-${incidencia.id}`}>
                  {t(idioma, 'portal.tituloIncidencia')}
                </label>
                <input
                  id={`ed-tit-${incidencia.id}`}
                  type="text"
                  value={titulo}
                  maxLength={300}
                  onChange={(e) => setTitulo(e.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor={`ed-desc-${incidencia.id}`}>{t(idioma, 'portal.detalles')}</label>
                <textarea
                  id={`ed-desc-${incidencia.id}`}
                  rows={3}
                  value={descripcion}
                  maxLength={2000}
                  onChange={(e) => setDescripcion(e.target.value)}
                />
              </div>
              <div className="fila-flex">
                <button type="button" disabled={titulo.trim().length < 3} onClick={guardarTexto}>
                  {t(idioma, 'portal.guardar')}
                </button>
                <button
                  type="button"
                  className="secundario"
                  onClick={() => {
                    setTitulo(incidencia.titulo);
                    setDescripcion(incidencia.descripcion ?? '');
                    setEditando(false);
                  }}
                >
                  {t(idioma, 'portal.cancelar')}
                </button>
              </div>
            </div>
          ) : (
            <>
              {incidencia.descripcion && <p>{incidencia.descripcion}</p>}
              {incidencia.abierta && !soloMirar && (
                <p className="suave pequeno">
                  <button type="button" className="enlace" onClick={() => setEditando(true)}>
                    {t(idioma, 'portal.editar')}
                  </button>{' '}
                  · {t(idioma, 'portal.editarPie')}
                </p>
              )}
            </>
          )}

          <h4>{t(idioma, 'portal.fotos')}</h4>
          {incidencia.fotos.length > 0 && (
            <div className="portal-fotos">
              {incidencia.fotos.map((f) => (
                <a key={f} href={api.urlAdjunto(f)} target="_blank" rel="noreferrer">
                  <img src={api.urlAdjunto(f)} alt="" />
                </a>
              ))}
            </div>
          )}
          {/* Una foto vale mas que tres mensajes describiendo la mancha. */}
          {!soloMirar && (
          <div className="fila-flex">
            <label className="boton-fichero" htmlFor={`fotos-${incidencia.id}`}>
              <Icono nombre="camara" /> {t(idioma, subiendo ? 'app.cargando' : 'portal.anadirFotos')}
            </label>
            <input
              id={`fotos-${incidencia.id}`}
              type="file"
              accept="image/*"
              multiple
              className="solo-lectores"
              disabled={subiendo}
              onChange={(e) => {
                const elegidas = [...(e.target.files ?? [])];
                e.target.value = '';
                subirFotos(elegidas);
              }}
            />
          </div>
          )}

          {/*
            EL HILO ES DEL DUENO. Es su conversacion privada con nosotros sobre
            su casa; un tercero que mira como va la obra no tiene por que
            leerla. El servidor ya no se la manda — esto es solo no dejar el
            titulo de una seccion vacia.
          */}
          {!soloMirar && (
          <>
          <h4>{t(idioma, 'portal.mensajes')}</h4>
          {incidencia.mensajes.length === 0 ? (
            <p className="suave">{t(idioma, 'portal.sinMensajes')}</p>
          ) : (
            <div className="portal-hilo">
              {incidencia.mensajes.map((m) => (
                <div key={m.id} className={`burbuja ${m.deEquipo ? 'equipo' : 'mia'}`}>
                  <p>{m.cuerpo}</p>
                  <small>
                    {m.autor} · {fechaLarga(m.creadoEn, idioma)}
                  </small>
                </div>
              ))}
            </div>
          )}

          <form
            className="portal-escribir"
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
          >
            <input
              type="text"
              value={texto}
              placeholder={t(idioma, 'portal.escribeMensaje')}
              onChange={(e) => setTexto(e.target.value)}
              aria-label={t(idioma, 'portal.escribeMensaje')}
            />
            <button type="submit" disabled={enviando || !texto.trim()}>
              {t(idioma, 'portal.enviar')}
            </button>
          </form>
          </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Una sección que se abre y se cierra.
 *
 * El portal se mira desde el móvil, casi siempre para una cosa concreta. Con
 * todo desplegado hay que bajar media pantalla para llegar a lo que se venía a
 * ver; plegado, la portada entera cabe de un vistazo y cada bloque se abre
 * cuando hace falta. Incidencias empieza abierta si hay alguna viva: eso SÍ se
 * viene a ver.
 */
function Plegable({
  titulo,
  contador,
  inicial = false,
  children,
}: {
  titulo: string;
  contador?: number;
  inicial?: boolean;
  children: React.ReactNode;
}) {
  const [abierta, setAbierta] = useState(inicial);
  return (
    <section className="tarjeta portal-plegable">
      <button
        type="button"
        className="portal-plegable-cabeza"
        aria-expanded={abierta}
        onClick={() => setAbierta((v) => !v)}
      >
        <span aria-hidden="true">{abierta ? '−' : '+'}</span>
        <h2>{titulo}</h2>
        {contador !== undefined && <span className="pastilla neutra">{contador}</span>}
      </button>
      {abierta && <div className="portal-plegable-cuerpo">{children}</div>}
    </section>
  );
}

/**
 * EL PORTAL, PARA DOS PERSONAS DISTINTAS.
 *
 * El propietario y el promotor miran la misma casa. Uno es el dueño: avisa de
 * una fuga, dice cuándo viene, contesta en el hilo. El otro es un observador
 * —construyó la casa, la financia, la comparte— y viene a ver cómo va.
 *
 * `soloMirar` quita todo lo que escribe. No se apagan los botones: se quitan.
 * Un botón apagado es una promesa de que algún día se podrá, y aquí no es que
 * ahora no se pueda: es que no le corresponde.
 *
 * Y esto es la mitad visible. La otra está en el servidor, que sigue exigiendo
 * ser el dueño en cada una de las funciones que escriben.
 */
export function PortalCliente({
  idioma,
  nombre,
  soloMirar = false,
}: {
  idioma: Idioma;
  nombre: string;
  soloMirar?: boolean;
}) {
  const [propiedades, setPropiedades] = useState<PortalPropiedad[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<null | 'incidencia' | 'estancia' | 'facturacion'>(null);
  const [activa, setActiva] = useState<string | null>(null);

  const cargar = () => {
    api
      .portal()
      .then((r) => {
        setPropiedades(r.portal.propiedades);
        setActiva((a) => a ?? r.portal.propiedades[0]?.id ?? null);
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      );
  };

  useEffect(cargar, [idioma]);

  const saludo = (() => {
    const h = new Date().getHours();
    if (h < 13) return t(idioma, 'portal.buenosDias', { nombre });
    if (h < 20) return t(idioma, 'portal.buenasTardes', { nombre });
    return t(idioma, 'portal.buenasNoches', { nombre });
  })();

  if (error) {
    return (
      <p className="aviso-demo" role="alert">
        {error}
      </p>
    );
  }
  if (propiedades === null) return <p className="suave">{t(idioma, 'app.cargando')}</p>;

  const propiedad = propiedades.find((p) => p.id === activa) ?? propiedades[0];

  return (
    <div className="portal">
      <header className="portal-cabeza">
        <h1>{saludo}</h1>
        <p className="suave">{t(idioma, 'portal.tusPropiedades')}</p>
      </header>

      {propiedades.length === 0 && <p className="suave">{t(idioma, 'portal.sinPropiedades')}</p>}

      {propiedades.length > 1 && (
        <div className="pestanas" role="tablist">
          {propiedades.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === propiedad?.id}
              onClick={() => setActiva(p.id)}
            >
              {p.nombre}
            </button>
          ))}
        </div>
      )}

      {propiedad && (
        <>
          {/* --- La casa, con su foto y su estado -------------------------- */}
          <section className="portal-hero">
            {/* Su casa, con SU foto. La ilustración es el respaldo de la que
                todavía no tiene ninguna, no la portada por defecto de todas. */}
            {propiedad.fotoPortadaUrl ? (
              <img className="portal-hero-foto" src={propiedad.fotoPortadaUrl} alt="" />
            ) : (
              <FotoVilla semilla={semillaDe(propiedad.id)} alto={260} etiqueta={false} />
            )}
            <div className="portal-hero-texto">
              <h2>{propiedad.nombre}</h2>
              <p>
                {propiedad.zona ?? ''} · {propiedad.codigo}
              </p>
            </div>
            {/*
              EL SEMAFORO, CON SU COLOR. Se pedia la clase «amarillo», que no
              existe —la del color es «ambar»—, y la pastilla salia sin color y
              con letra oscura sobre la foto: no se veia ni se entendia. Verde
              sin incidencias urgentes, ambar con alguna prioritaria (N2), rojo
              con alguna urgencia (N1).
            */}
            <span
              className={`pastilla portal-semaforo ${CLASE_SEMAFORO[propiedad.semaforo] ?? 'verde'}`}
            >
              {t(idioma, `semaforo.${propiedad.semaforo}`)}
            </span>
          </section>

          {/*
            LAS DOS COSAS QUE EL DUEÑO VIENE A HACER, arriba y grandes: avisar
            de algo que ha visto, y avisar de que viene. Escondidas detrás de un
            enlace pequeño al final de la página, no se usan.
          */}
          {soloMirar ? (
            <p className="suave portal-observador">{t(idioma, 'portal.soloMirar')}</p>
          ) : (
            <div className="portal-acciones">
              <button type="button" onClick={() => setModal('incidencia')}>
                <Icono nombre="aviso" />
                {t(idioma, 'portal.notificarIncidencia')}
              </button>
              <button type="button" onClick={() => setModal('estancia')}>
                <Icono nombre="calendario" />
                {t(idioma, 'portal.notificarVisita')}
              </button>
            </div>
          )}

          <div className="rejilla kpis">
            <Kpi
              etiqueta={t(idioma, 'portal.ultimaVisita')}
              valor={propiedad.ultimaVisita ? formatearDia(propiedad.ultimaVisita) : '—'}
              pie={propiedad.ultimaVisita ? undefined : t(idioma, 'portal.sinVisitas')}
            />
            <Kpi
              etiqueta={t(idioma, 'portal.proximaVisitaCorta')}
              valor={propiedad.proximaVisita ? formatearDia(propiedad.proximaVisita) : '—'}
            />
            <Kpi
              etiqueta={t(idioma, 'portal.incidenciasAbiertas')}
              valor={String(propiedad.incidencias.filter((i) => i.abierta).length)}
              pie={t(idioma, 'portal.enCurso')}
            />
            {/*
              SI EL DUENO DEBE UNA FACTURA no es el estado de la casa, es su
              situacion con nosotros. Un promotor que mira como va la obra no
              tiene por que enterarse.
            */}
            {!soloMirar && (
              <Kpi
                etiqueta={t(idioma, 'portal.alCorriente')}
                valor={t(idioma, propiedad.alCorrienteDePago ? 'rapido.si' : 'rapido.no')}
              />
            )}
          </div>

          {/* --- El resumen, escrito por la IA sobre datos reales ---------- */}
          <ResumenIa idioma={idioma} propiedadId={propiedad.id} />

          {/* --- Sus incidencias ------------------------------------------- */}
          <Plegable
            titulo={t(idioma, 'portal.misIncidencias')}
            contador={propiedad.incidencias.length}
            inicial={propiedad.incidencias.some((i) => i.abierta)}
          >
            {propiedad.incidencias.length === 0 ? (
              <p className="suave">{t(idioma, 'portal.sinIncidencias')}</p>
            ) : (
              <IncidenciasDelDueno
                idioma={idioma}
                incidencias={propiedad.incidencias}
                soloMirar={soloMirar}
                onCambio={cargar}
              />
            )}
          </Plegable>

          {/* --- Su estancia ----------------------------------------------- */}
          <Plegable titulo={t(idioma, 'portal.tuEstancia')} contador={propiedad.estancias.length}>
            {propiedad.estancias.length === 0 ? (
              <p className="suave">{t(idioma, 'portal.sinEstancias')}</p>
            ) : (
              <ul className="portal-estancias">
                {propiedad.estancias.map((e) => (
                  <li key={e.id}>
                    <Icono nombre="calendario" />
                    <span>
                      {formatearDia(e.desde)}
                      {e.hasta ? ` → ${formatearDia(e.hasta)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {!soloMirar && (
              <div className="fila-flex">
                <button type="button" className="secundario" onClick={() => setModal('estancia')}>
                  <Icono nombre="calendario" /> {t(idioma, 'portal.avisarLlegada')}
                </button>
              </div>
            )}
          </Plegable>

          {/* --- Los papeles de su casa -------------------------------------- */}
          {/*
            EL CONTRATO, LA FACTURA DEL FONTANERO, LA REVISIÓN DEL ASCENSOR.

            Todo eso se subía a la ficha de la propiedad y se quedaba dentro, y
            el dueño los pedía por correo uno a uno. Aquí están los suyos, del
            más nuevo al más viejo, que es como se buscan. Se abren dentro de la
            aplicación: un propietario que toca un PDF y acaba en otra pestaña
            sin forma de volver no vuelve a tocarlo.
          */}
          <Plegable titulo={t(idioma, 'portal.documentos')} contador={propiedad.documentos.length}>
            {propiedad.documentos.length === 0 ? (
              <p className="suave">{t(idioma, 'portal.sinDocumentos')}</p>
            ) : (
              <div className="lista">
                {propiedad.documentos.map((d) => {
                  const destino = d.adjuntoId ? api.urlAdjunto(d.adjuntoId) : d.url;
                  return (
                    <div key={d.id} className="fila">
                      <Icono nombre="documento" />
                      <span className="crecer">
                        {destino ? (
                          <a href={destino} target="_blank" rel="noreferrer" className="enlace">
                            {d.titulo}
                          </a>
                        ) : (
                          <b>{d.titulo}</b>
                        )}
                        <small className="suave">{formatearDia(d.creadoEn.slice(0, 10))}</small>
                      </span>
                      <span className="chip-area">{t(idioma, `tipoDocumento.${d.tipo}`)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </Plegable>

          {/* --- Datos de facturación --------------------------------------- */}
          {!soloMirar && (
            <Plegable titulo={t(idioma, 'portal.facturacion')}>
              {propiedad.datosFacturacion ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>{propiedad.datosFacturacion}</p>
              ) : (
                <p className="suave">{t(idioma, 'portal.facturacionPie')}</p>
              )}
              <div className="fila-flex">
                <button
                  type="button"
                  className="secundario"
                  onClick={() => setModal('facturacion')}
                >
                  {t(idioma, 'portal.editar')}
                </button>
              </div>
            </Plegable>
          )}

          {modal === 'incidencia' && (
            <ModalReportar
              idioma={idioma}
              propiedadId={propiedad.id}
              onHecho={cargar}
              onCerrar={() => setModal(null)}
            />
          )}
          {modal === 'estancia' && (
            <ModalEstancia
              idioma={idioma}
              propiedadId={propiedad.id}
              onHecho={cargar}
              onCerrar={() => setModal(null)}
            />
          )}
          {modal === 'facturacion' && (
            <ModalFacturacion
              idioma={idioma}
              propiedadId={propiedad.id}
              inicial={propiedad.datosFacturacion ?? ''}
              onHecho={cargar}
              onCerrar={() => setModal(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

function ModalReportar({
  idioma,
  propiedadId,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  propiedadId: string;
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [nivel, setNivel] = useState<NivelIncidencia>(3);
  const [fotos, setFotos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'portal.notificarIncidencia')} icono="aviso" onCerrar={onCerrar}>
      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (titulo.trim().length < 3) return;
          setEnviando(true);
          setError(null);
          void (async () => {
            try {
              const { incidencia } = await api.portalReportar(propiedadId, {
                titulo: titulo.trim(),
                nivel,
                ...(descripcion.trim() ? { descripcion: descripcion.trim() } : {}),
              });
              /*
               * Las fotos van DESPUES, con la incidencia ya creada: sin ella no
               * hay a que colgarlas. Si una falla, el parte ya esta dentro.
               */
              for (const f of fotos) {
                await api.subirAdjunto('INCIDENCIA', incidencia.id, f);
              }
              onHecho();
              onCerrar();
            } catch (f: unknown) {
              setError(
                f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor'),
              );
            } finally {
              setEnviando(false);
            }
          })();
        }}
      >
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="campo">
          <label htmlFor="rep-titulo">{t(idioma, 'portal.queOcurre')}</label>
          <input
            id="rep-titulo"
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder={t(idioma, 'portal.queOcurrePista')}
            required
          />
        </div>
        <div className="campo">
          <label htmlFor="rep-desc">{t(idioma, 'portal.detalles')}</label>
          <textarea
            id="rep-desc"
            rows={4}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </div>
        {/*
          El dueno dice la urgencia que ve. No es la ultima palabra —el equipo la
          revisa— pero preguntarsela y luego ignorarla seria peor que no
          preguntar: quien reporta una fuga a las tres de la manana lo sabe.
        */}
        <div className="campo">
          <span className="etiqueta-campo">{t(idioma, 'portal.urgencia')}</span>
          <div className="niveles" role="group" aria-label={t(idioma, 'portal.urgencia')}>
            {([1, 2, 3, 4] as NivelIncidencia[]).map((n) => (
              <button
                key={n}
                type="button"
                className={`nivel-boton n${n}`}
                aria-pressed={nivel === n}
                onClick={() => setNivel(n)}
              >
                <b>N{n}</b>
                <small>{t(idioma, `incidencia.nivel.${n}`)}</small>
              </button>
            ))}
          </div>
          <small className="suave">{t(idioma, 'portal.urgenciaPie')}</small>
        </div>

        <div className="campo">
          <span className="etiqueta-campo">{t(idioma, 'portal.fotos')}</span>
          <div className="fila-flex">
            <label className="boton-fichero" htmlFor="rep-fotos">
              <Icono nombre="camara" /> {t(idioma, 'portal.anadirFotos')}
            </label>
            <input
              id="rep-fotos"
              type="file"
              accept="image/*"
              multiple
              className="solo-lectores"
              onChange={(e) => {
                const elegidas = [...(e.target.files ?? [])];
                e.target.value = '';
                if (elegidas.length > 0) setFotos((p) => [...p, ...elegidas]);
              }}
            />
          </div>
          {fotos.length > 0 && (
            <div className="fila-flex">
              {fotos.map((f, i) => (
                <button
                  key={`${f.name}-${String(i)}`}
                  type="button"
                  className="secundario"
                  onClick={() => setFotos((p) => p.filter((_, j) => j !== i))}
                >
                  {f.name} ✕
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="suave" style={{ fontSize: '0.8125rem' }}>
          {t(idioma, 'portal.reportarNota')}
        </p>
        <button type="submit" className="acceso-entrar" disabled={enviando}>
          {t(idioma, enviando ? 'app.cargando' : 'portal.enviar')}
        </button>
      </form>
    </Modal>
  );
}

function ModalEstancia({
  idioma,
  propiedadId,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  propiedadId: string;
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'portal.avisarLlegada')} icono="calendario" onCerrar={onCerrar}>
      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!desde) return;
          setEnviando(true);
          api
            .portalEstancia(propiedadId, { desde, hasta: hasta || undefined })
            .then(() => {
              onHecho();
              onCerrar();
            })
            .catch(() => undefined)
            .finally(() => setEnviando(false));
        }}
      >
        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="est-desde">{t(idioma, 'portal.llegada')}</label>
            <input
              id="est-desde"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              required
            />
          </div>
          <div className="campo">
            <label htmlFor="est-hasta">{t(idioma, 'portal.salida')}</label>
            <input
              id="est-hasta"
              type="date"
              value={hasta}
              min={desde || undefined}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
        </div>
        <p className="suave" style={{ fontSize: '0.8125rem' }}>
          {t(idioma, 'portal.estanciaNota')}
        </p>
        <button type="submit" className="acceso-entrar" disabled={enviando}>
          {t(idioma, 'portal.enviar')}
        </button>
      </form>
    </Modal>
  );
}

function ModalFacturacion({
  idioma,
  propiedadId,
  inicial,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  propiedadId: string;
  inicial: string;
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [datos, setDatos] = useState(inicial);
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'portal.facturacion')} onCerrar={onCerrar}>
      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (datos.trim().length < 2) return;
          setEnviando(true);
          api
            .portalFacturacion(propiedadId, datos.trim())
            .then(() => {
              onHecho();
              onCerrar();
            })
            .catch(() => undefined)
            .finally(() => setEnviando(false));
        }}
      >
        <div className="campo">
          <label htmlFor="fac-datos">{t(idioma, 'portal.facturacionCampo')}</label>
          <textarea
            id="fac-datos"
            rows={5}
            value={datos}
            onChange={(e) => setDatos(e.target.value)}
            placeholder={t(idioma, 'portal.facturacionPista')}
          />
        </div>
        <button type="submit" className="acceso-entrar" disabled={enviando}>
          {t(idioma, 'portal.guardar')}
        </button>
      </form>
    </Modal>
  );
}

// --- El resumen con IA ---------------------------------------------------------

/**
 * Un resumen en dos o tres frases de como esta su casa.
 *
 * Se pide al ABRIR la seccion, no al cargar el portal: cuesta una llamada al
 * proveedor, y quien entra a mirar una foto no tiene por que pagarla.
 *
 * Lo escribe el modelo, pero NO aporta hechos: recibe un parte ya calculado con
 * los mismos datos que se ven mas abajo y solo lo redacta. Por eso el resumen no
 * puede contradecir la pantalla.
 *
 * Sin proveedor configurado no aparece nada. Antes que un texto de plantilla
 * disfrazado de resumen, ninguno: prometer «resumen con IA» y entregar tres
 * frases hechas es engañar a quien lo lee.
 */
function ResumenIa({ idioma, propiedadId }: { idioma: Idioma; propiedadId: string }) {
  const [abierta, setAbierta] = useState(false);
  const [resumen, setResumen] = useState<string | null>(null);
  const [pedido, setPedido] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abrir = () => {
    const ahora = !abierta;
    setAbierta(ahora);
    if (!ahora || pedido) return;
    setPedido(true);
    setCargando(true);
    api
      .portalResumen(propiedadId)
      .then((r) => setResumen(r.resumen))
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setCargando(false));
  };

  return (
    <section className="tarjeta portal-plegable">
      <button
        type="button"
        className="portal-plegable-cabeza"
        aria-expanded={abierta}
        onClick={abrir}
      >
        <span aria-hidden="true">{abierta ? '−' : '+'}</span>
        <h2>{t(idioma, 'portal.resumen')}</h2>
      </button>
      {abierta && (
        <div className="portal-plegable-cuerpo">
          {cargando && <p className="suave">{t(idioma, 'app.cargando')}</p>}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {!cargando && !error && resumen === null && (
            <p className="suave">{t(idioma, 'portal.resumenNoDisponible')}</p>
          )}
          {resumen !== null && (
            <>
              <p className="prosa">{resumen}</p>
              <small className="suave">{t(idioma, 'portal.resumenPie')}</small>
            </>
          )}
        </div>
      )}
    </section>
  );
}
